import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  CircularProgress,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import api from '../api/client';

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];

  // Simple CSV parser: splits by comma, respecting quoted fields
  const parseLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]).map((h) => h.toLowerCase().trim());
  const emailIdx = headers.findIndex((h) => h.includes('email') || h === 'e-mail');
  const firstIdx = headers.findIndex((h) => h.includes('first') || h === 'imię' || h === 'imie');
  const lastIdx = headers.findIndex((h) => h.includes('last') || h === 'nazwisko');
  const deptIdx = headers.findIndex((h) => h.includes('department') || h === 'dział' || h === 'dzial');
  const locIdx = headers.findIndex((h) => h.includes('location') || h === 'lokalizacja');

  return lines.slice(1).map((line, index) => {
    const cols = parseLine(line);
    const email = cols[emailIdx] || '';
    const firstName = cols[firstIdx] || '';
    const lastName = cols[lastIdx] || '';
    const department = cols[deptIdx] || '';
    const location = cols[locIdx] || '';
    const displayName = `${firstName} ${lastName}`.trim() || email.split('@')[0];
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    return {
      email,
      firstName,
      lastName,
      displayName,
      department,
      location,
      status: email && displayName && emailValid ? 'ok' : 'error',
      row: index + 2,
    };
  });
}

function ImportEmployees() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'Plik jest za duży. Maksymalnie 5 MB.', severity: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const rows = parseCSV(event.target.result || '');
      setPreview(rows);
      if (rows.length === 0) {
        setSnackbar({ open: true, message: 'Nie znaleziono danych w pliku CSV.', severity: 'warning' });
      }
    };
    reader.onerror = () => setSnackbar({ open: true, message: 'Błąd odczytu pliku.', severity: 'error' });
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!preview || preview.length === 0) return;
    const validUsers = preview.filter((row) => row.status === 'ok');
    if (validUsers.length === 0) {
      setSnackbar({ open: true, message: 'Brak poprawnych wierszy do importu.', severity: 'warning' });
      return;
    }

    setImporting(true);
    setProgress(0);
    try {
      const { data } = await api.post('/api/tenants/me/users/bulk', {
        users: validUsers.map((u) => ({
          email: u.email,
          displayName: u.displayName,
          role: 'employee',
        })),
      });

      const created = data.created || 0;
      const errors = data.errors || [];
      if (errors.length > 0) {
        setPreview((prev) =>
          prev.map((row) => {
            const err = errors.find((e) => e.email === row.email);
            return err ? { ...row, status: 'error', error: err.error } : row;
          })
        );
      }
      setProgress(100);
      setSnackbar({
        open: true,
        message: `Zaimportowano ${created} z ${validUsers.length} pracowników.${errors.length > 0 ? ` Błędów: ${errors.length}` : ''}`,
        severity: errors.length > 0 ? 'warning' : 'success',
      });
      if (created > 0) {
        setTimeout(() => navigate('/employees'), errors.length > 0 ? 2000 : 1200);
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd importu: ${err.body?.error || err.message}`, severity: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const okCount = preview?.filter((row) => row.status === 'ok').length || 0;
  const errorCount = preview?.filter((row) => row.status === 'error').length || 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 720, borderRadius: 4 }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Import pracowników
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Wgraj plik CSV z kolumnami: e-mail, imię, nazwisko, dział, lokalizacja.
          </Typography>

          <input
            type="file"
            accept=".csv,text/csv"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderStyle: 'dashed',
              p: { xs: 3, sm: 4 },
              textAlign: 'center',
              mb: 3,
              cursor: 'pointer',
            }}
            onClick={handleUploadClick}
          >
            <UploadFileIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="body1" fontWeight={500}>
              Kliknij, aby wgrać plik CSV
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Maksymalny rozmiar: 5 MB
            </Typography>
          </Paper>

          {preview && (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`Poprawnych: ${okCount}`}
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<ErrorIcon />}
                  label={`Błędów: ${errorCount}`}
                  color="error"
                  variant="outlined"
                />
              </Box>

              {errorCount > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Napraw błędy przed importem lub pomiń wiersze oznaczone jako błędne.
                </Alert>
              )}

              {importing && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Importowanie…
                  </Typography>
                </Box>
              )}

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>E-mail</TableCell>
                      <TableCell>Imię</TableCell>
                      <TableCell>Nazwisko</TableCell>
                      <TableCell>Dział</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.firstName}</TableCell>
                        <TableCell>{row.lastName}</TableCell>
                        <TableCell>{row.department}</TableCell>
                        <TableCell>
                          {row.status === 'ok' ? (
                            <Chip icon={<CheckCircleIcon />} label="OK" color="success" size="small" />
                          ) : (
                            <Chip icon={<ErrorIcon />} label="Błąd" color="error" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                <Button onClick={() => setPreview(null)} sx={{ order: { xs: 2, sm: 1 } }} disabled={importing}>
                  Wyczyść
                </Button>
                <Button
                  variant="contained"
                  onClick={handleImport}
                  disabled={importing || okCount === 0}
                  startIcon={importing ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ order: { xs: 1, sm: 2 } }}
                >
                  {importing ? 'Importowanie…' : `Zaimportuj ${okCount > 0 ? `(${okCount})` : ''}`}
                </Button>
              </Box>

              <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                  {snackbar.message}
                </Alert>
              </Snackbar>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ImportEmployees;
